import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { ArrowRightIcon } from '../lib/icons.tsx';

export interface BreadcrumbsProps extends ComponentProps<'nav'> {
  children?: ReactNode;
}

/**
 * The trail above a page title.
 *
 * Separated by an **arrow, not a slash** — the product's one deliberate
 * departure from the usual, and the thing that makes its headers recognisable.
 * The separators are drawn by this component, so callers pass only the crumbs.
 */
export function Breadcrumbs({ className, children, ...props }: BreadcrumbsProps) {
  const crumbs = Array.isArray(children) ? children : [children];

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn('flex items-center gap-3 text-base text-ink-mid', className)}
      {...props}
    >
      {crumbs.filter(Boolean).map((crumb, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: crumbs are positional and never reorder
        <span className="flex items-center gap-3" key={index}>
          {index > 0 ? <ArrowRightIcon className="size-4 shrink-0 text-ink-faint" /> : null}
          {crumb}
        </span>
      ))}
    </nav>
  );
}

/** A crumb you can go back to. */
export function Crumb({ className, ...props }: ComponentProps<'a'>) {
  return (
    <a className={cn('no-underline transition-colors hover:text-ink', className)} {...props} />
  );
}

/** The page you are on — the last crumb, and the only bold one. */
export function CurrentCrumb({ className, ...props }: ComponentProps<'span'>) {
  return (
    <span aria-current="page" className={cn('font-semibold text-ink', className)} {...props} />
  );
}
